import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ExperienceTheme, ExperienceThemeService } from '../../services/experience-theme.service';
import { PdfDropService } from '../../services/pdf-drop.service';
import { PdfDropComponent } from '../../components/pdf-drop/pdf-drop.component';
import {
  PdfPreviewContext,
  PdfPreviewDrawerComponent,
} from '../../components/pdf-preview-drawer/pdf-preview-drawer.component';

type ReportSource = 'gavekal' | 'bloomberg' | 'bofa' | 'jpmorgan' | 'gs';
type ViewDir = 'up' | 'down' | 'flat';
type MatchKind = 'matched' | 'unmatched' | 'partial';
type ViewKind = 'Absolute' | 'Relative';

interface ExtractedView {
  id: string;
  asset: string;
  assetName: string;
  matched: MatchKind;
  viewType: ViewKind;
  dir: ViewDir;
  value: number;
  confidence: number;
  confidenceQual: string;
  excerpt: string;
  page: number;
  accepted: boolean;
  rejected: boolean;
}

interface ResearchReport {
  id: string;
  title: string;
  source: ReportSource;
  sourceLabel: string;
  author: string;
  date: string;
  pageCount: number;
  status: 'processed' | 'processing';
  uploadedAgo: string;
  summary: string;
  viewsCount: number;
  appliedCount: number;
  extractedViews: ExtractedView[];
  fresh?: boolean;
}

interface SourceMeta {
  short: string;
  titleShort: string;
  accent: string;
}

const SOURCE_META: Record<ReportSource, SourceMeta> = {
  gavekal: { short: 'Gavekal', titleShort: 'Bond-Free Portfolio', accent: '#B23A2E' },
  bloomberg: { short: 'Bloomberg', titleShort: 'AI Infrastructure', accent: '#0A0A0A' },
  bofa: { short: 'BofA', titleShort: 'European Defensives', accent: '#D0273A' },
  jpmorgan: { short: 'J.P.Morgan', titleShort: 'EM Local FI', accent: '#0F3878' },
  gs: { short: 'GS', titleShort: '', accent: '#7F7F7F' },
};

const SHORT_LABELS: Record<ReportSource, string> = {
  gavekal: 'Gavekal',
  bloomberg: 'Bloomberg',
  bofa: 'BofA',
  jpmorgan: 'JPM',
  gs: 'GS',
};

const BASE_REPORTS: ResearchReport[] = [
  {
    id: 'gavekal-q2-2026',
    title: 'The Bond-Free Portfolio, Q2 2026 Outlook',
    source: 'gavekal',
    sourceLabel: 'Gavekal Research',
    author: 'Louis-Vincent Gave · Anatole Kaletsky',
    date: '2026-04-15',
    pageCount: 42,
    status: 'processed',
    uploadedAgo: '2 days ago',
    summary:
      'Strategy piece arguing for platform equities + real assets over duration, with emerging-market tilt and selective energy exposure.',
    viewsCount: 7,
    appliedCount: 3,
    extractedViews: [
      { id: 'v1', asset: 'NVDA', assetName: 'NVIDIA Corp.', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 18, confidence: 75, confidenceQual: 'Strong conviction', excerpt: 'We reiterate a strong conviction long on NVIDIA given durable platform moats and insatiable AI inference demand through 2027.', page: 11, accepted: true, rejected: false },
      { id: 'v2', asset: 'GOLD', assetName: 'Gold (spot)', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 9, confidence: 85, confidenceQual: 'Very high conviction', excerpt: 'Gold remains our preferred hedge as real rates peak and central bank diversification accelerates — we target $3,000+ into year-end.', page: 18, accepted: true, rejected: false },
      { id: 'v3', asset: 'TLT', assetName: 'iShares 20+ Yr Treasury', matched: 'matched', viewType: 'Absolute', dir: 'down', value: -6, confidence: 70, confidenceQual: 'High conviction', excerpt: 'Long-duration Treasuries face persistent headwinds from structural inflation and supply pressure. We remain underweight duration.', page: 23, accepted: true, rejected: false },
      { id: 'v4', asset: 'XLE', assetName: 'Energy Select SPDR', matched: 'matched', viewType: 'Relative', dir: 'up', value: 5, confidence: 55, confidenceQual: 'Moderate conviction', excerpt: 'Energy equities offer cheap optionality on sticky oil prices. Relative tilt vs. broad market warranted but risk-managed.', page: 29, accepted: false, rejected: false },
      { id: 'v5', asset: 'EEM', assetName: 'MSCI Emerging Markets', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 12, confidence: 60, confidenceQual: 'Moderate-to-high conviction', excerpt: 'EM equities enter a multi-year bull cycle as the dollar peaks and Chinese stimulus re-accelerates domestic demand.', page: 32, accepted: false, rejected: false },
      { id: 'v6', asset: 'PLATFORM-BASKET', assetName: 'Platform equities basket', matched: 'unmatched', viewType: 'Absolute', dir: 'up', value: 15, confidence: 65, confidenceQual: 'High conviction', excerpt: 'Our custom platform basket (Google, Meta, Microsoft, Amazon, Apple, NVDA) should compound at 15%+ through 2027.', page: 35, accepted: false, rejected: false },
      { id: 'v7', asset: 'JPY', assetName: 'Japanese Yen', matched: 'partial', viewType: 'Relative', dir: 'down', value: -4, confidence: 50, confidenceQual: 'Low-to-moderate conviction', excerpt: 'Yen strength into H2 remains our baseline but conviction has softened with BoJ communication muddied.', page: 38, accepted: false, rejected: false },
    ],
  },
  {
    id: 'bloomberg-ai-2026',
    title: 'AI Infrastructure: From Hype to Cashflow',
    source: 'bloomberg',
    sourceLabel: 'Bloomberg Intelligence',
    author: 'Mandeep Singh · Anurag Rana',
    date: '2026-04-08',
    pageCount: 28,
    status: 'processed',
    uploadedAgo: '1 week ago',
    summary:
      'Deep dive on AI capex monetisation, datacenter buildout, and winners across the semi + hyperscaler stack.',
    viewsCount: 5,
    appliedCount: 2,
    extractedViews: [
      { id: 'b1', asset: 'NVDA', assetName: 'NVIDIA Corp.', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 22, confidence: 80, confidenceQual: 'Very high conviction', excerpt: 'Data-center revenue grows 38% YoY through 2026 on Blackwell ramp and sustained hyperscaler spend.', page: 6, accepted: true, rejected: false },
      { id: 'b2', asset: 'AAPL', assetName: 'Apple, Inc.', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 7, confidence: 45, confidenceQual: 'Moderate conviction', excerpt: 'AI integration drives an iPhone upgrade supercycle, though the monetisation path remains gradual.', page: 14, accepted: false, rejected: false },
      { id: 'b3', asset: 'AMZN', assetName: 'Amazon', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 14, confidence: 70, confidenceQual: 'High conviction', excerpt: 'AWS re-acceleration from AI workloads should drive operating margin expansion into 2027.', page: 19, accepted: true, rejected: false },
      { id: 'b4', asset: 'SMH', assetName: 'Semiconductor ETF', matched: 'matched', viewType: 'Relative', dir: 'up', value: 6, confidence: 60, confidenceQual: 'Moderate-to-high conviction', excerpt: 'Semis as a basket continue to outperform on favorable supply/demand through the cycle mid-point.', page: 22, accepted: false, rejected: false },
      { id: 'b5', asset: 'POWER-UTILS', assetName: 'Power utilities sleeve', matched: 'unmatched', viewType: 'Absolute', dir: 'up', value: 11, confidence: 65, confidenceQual: 'High conviction', excerpt: 'Datacenter power demand is the most underappreciated AI derivative trade.', page: 26, accepted: false, rejected: false },
    ],
  },
  {
    id: 'bofa-eur-defensive',
    title: 'European Defensives: A Late-Cycle Hedge',
    source: 'bofa',
    sourceLabel: 'Bank of America Global Research',
    author: 'Sebastian Raedler · Milla Savova',
    date: '2026-03-22',
    pageCount: 19,
    status: 'processed',
    uploadedAgo: '3 weeks ago',
    summary:
      'Call to rotate into EUR-listed defensive staples and healthcare as US cyclicals look stretched.',
    viewsCount: 4,
    appliedCount: 0,
    extractedViews: [
      { id: 'e1', asset: 'NESN.SW', assetName: 'Nestlé SA', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 8, confidence: 70, confidenceQual: 'High conviction', excerpt: "Nestlé's valuation has reset to a 10-year low while pricing power remains intact.", page: 5, accepted: false, rejected: false },
      { id: 'e2', asset: 'NOVO-B.CO', assetName: 'Novo Nordisk', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 11, confidence: 65, confidenceQual: 'High conviction', excerpt: 'GLP-1 franchise still underappreciated on durability despite manufacturing noise.', page: 9, accepted: false, rejected: false },
      { id: 'e3', asset: 'SXXP', assetName: 'STOXX Europe 600', matched: 'matched', viewType: 'Relative', dir: 'up', value: 4, confidence: 50, confidenceQual: 'Moderate conviction', excerpt: 'European equities trade at a 25% discount to US peers — reversion trade increasingly attractive.', page: 12, accepted: false, rejected: false },
      { id: 'e4', asset: 'CYCLICALS-EU', assetName: 'European cyclicals', matched: 'unmatched', viewType: 'Relative', dir: 'down', value: -5, confidence: 55, confidenceQual: 'Moderate conviction', excerpt: 'Short European cyclicals as PMIs roll over into mid-year.', page: 17, accepted: false, rejected: false },
    ],
  },
  {
    id: 'jpm-em-local',
    title: 'EM Local FI: The Dollar-Peak Trade',
    source: 'jpmorgan',
    sourceLabel: 'J.P. Morgan Research',
    author: 'Saad Siddiqui · Jonny Goulden',
    date: '2026-02-11',
    pageCount: 34,
    status: 'processed',
    uploadedAgo: '2 months ago',
    summary:
      'Thesis on EM local-currency bonds as a high-carry play once DXY rolls over decisively.',
    viewsCount: 3,
    appliedCount: 0,
    extractedViews: [
      { id: 'j1', asset: 'EMB', assetName: 'iShares EM Bonds USD', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 6, confidence: 60, confidenceQual: 'Moderate-to-high conviction', excerpt: 'Hard-currency EM debt spreads still offer attractive carry over Treasuries.', page: 8, accepted: false, rejected: false },
      { id: 'j2', asset: 'EMLC', assetName: 'VanEck JPM EM Local', matched: 'matched', viewType: 'Absolute', dir: 'up', value: 10, confidence: 65, confidenceQual: 'High conviction', excerpt: 'Local-currency EM FI benefits doubly from rate cuts and FX appreciation as dollar weakens.', page: 14, accepted: false, rejected: false },
      { id: 'j3', asset: 'DXY', assetName: 'Dollar Index', matched: 'partial', viewType: 'Absolute', dir: 'down', value: -4, confidence: 55, confidenceQual: 'Moderate conviction', excerpt: 'DXY peak is in. Medium-term path lower on converging rate differentials.', page: 22, accepted: false, rejected: false },
    ],
  },
];

@Component({
  selector: 'app-exp-reports',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, PdfDropComponent, PdfPreviewDrawerComponent],
  templateUrl: './reports-experience.component.html',
})
export class ReportsExperienceComponent implements OnInit, OnDestroy {
  theme: ExperienceTheme = 'dark';

  reports: ResearchReport[] = [];
  activeReportId: string | null = null;

  pdfDropOpen = false;
  pdfPreviewOpen = false;
  pdfPreviewContext: PdfPreviewContext | null = null;

  toasts: Array<{ id: number; text: string }> = [];
  private toastId = 0;
  private processingTimer: any = null;
  private subs = new Subscription();

  readonly shortLabels = SHORT_LABELS;

  constructor(
    private themeService: ExperienceThemeService,
    private pdfDropService: PdfDropService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.theme = this.themeService.current;
    this.subs.add(
      this.themeService.current$.subscribe((t) => {
        this.theme = t;
        this.cdr.markForCheck();
      }),
    );

    this.reports = BASE_REPORTS.map((r) => this.cloneReport(r));

    const isFresh = this.route.snapshot.queryParamMap.get('new') === '1';
    let freshName: string | null = null;
    if (isFresh) {
      try {
        freshName = sessionStorage.getItem('cimalti-just-uploaded');
        sessionStorage.removeItem('cimalti-just-uploaded');
      } catch {
        freshName = null;
      }
      const processing: ResearchReport = {
        id: 'fresh-' + Date.now(),
        title: freshName || 'New research report.pdf',
        source: 'gavekal',
        sourceLabel: 'Processing…',
        author: 'Identifying source…',
        date: new Date().toISOString().slice(0, 10),
        pageCount: 0,
        status: 'processing',
        uploadedAgo: 'just now',
        summary:
          'Cimalti is extracting investment views, holdings, and performance data from your document.',
        viewsCount: 0,
        appliedCount: 0,
        extractedViews: [],
        fresh: true,
      };
      this.reports = [processing, ...this.reports];
      this.activeReportId = processing.id;

      this.processingTimer = setTimeout(() => {
        this.completeFreshUpload(processing.id, freshName);
      }, 2200);
    } else {
      this.activeReportId = this.reports[0]?.id || null;
    }

    this.subs.add(
      this.pdfDropService.open$.subscribe(() => {
        this.pdfDropOpen = true;
        this.cdr.markForCheck();
      }),
    );
  }

  ngOnDestroy(): void {
    if (this.processingTimer) clearTimeout(this.processingTimer);
    this.subs.unsubscribe();
  }

  get activeReport(): ResearchReport | null {
    return this.reports.find((r) => r.id === this.activeReportId) || null;
  }

  get totalReports(): number {
    return this.reports.length;
  }

  get totalViews(): number {
    return this.reports.reduce((s, r) => s + r.viewsCount, 0);
  }

  get totalApplied(): number {
    return this.reports.reduce((s, r) => s + r.appliedCount, 0);
  }

  get lastUploaded(): string {
    return this.reports[0]?.uploadedAgo || '—';
  }

  get acceptedCount(): number {
    const r = this.activeReport;
    if (!r) return 0;
    return r.extractedViews.filter((v) => v.accepted).length;
  }

  get activeViewCount(): number {
    return this.activeReport?.extractedViews.length || 0;
  }

  get activeThemeCount(): number {
    const r = this.activeReport;
    if (!r) return 0;
    const kinds = new Set<string>();
    r.extractedViews.forEach((v) => kinds.add(v.viewType));
    return kinds.size + 1;
  }

  get acceptedViews(): ExtractedView[] {
    return this.activeReport?.extractedViews.filter((v) => v.accepted) || [];
  }

  sourceShortLabel(r: ResearchReport): string {
    return this.shortLabels[r.source] || r.sourceLabel.split(' ')[0];
  }

  sourceMeta(r: ResearchReport): SourceMeta {
    const meta = SOURCE_META[r.source];
    if (meta) return meta;
    return { short: r.sourceLabel.split(' ')[0], titleShort: r.title.split(',')[0], accent: 'var(--accent)' };
  }

  dirArrow(v: ExtractedView): string {
    return v.dir === 'up' ? '▲' : v.dir === 'down' ? '▼' : '◆';
  }

  dirTone(v: ExtractedView): 'pos' | 'neg' | '' {
    return v.dir === 'up' ? 'pos' : v.dir === 'down' ? 'neg' : '';
  }

  valSign(v: ExtractedView): string {
    return v.dir === 'up' ? '+' : '';
  }

  matchedLabel(v: ExtractedView): string {
    return v.matched === 'matched' ? 'Matched' : v.matched === 'partial' ? 'Partial' : 'Unmatched';
  }

  rowClass(v: ExtractedView): string {
    if (v.accepted) return 'accepted';
    if (v.rejected) return 'rejected';
    return '';
  }

  viewTypeClass(v: ExtractedView): 'abs' | 'rel' {
    return v.viewType === 'Absolute' ? 'abs' : 'rel';
  }

  formatDate(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  trackByReport(_i: number, r: ResearchReport): string {
    return r.id;
  }

  trackByView(_i: number, v: ExtractedView): string {
    return v.id;
  }

  selectReport(id: string): void {
    this.activeReportId = id;
    this.cdr.markForCheck();
  }

  accept(v: ExtractedView): void {
    v.accepted = true;
    v.rejected = false;
    this.cdr.markForCheck();
  }

  reject(v: ExtractedView): void {
    v.accepted = false;
    v.rejected = true;
    this.cdr.markForCheck();
  }

  acceptAll(): void {
    const r = this.activeReport;
    if (!r) return;
    r.extractedViews.forEach((v) => {
      v.accepted = true;
      v.rejected = false;
    });
    this.cdr.markForCheck();
  }

  rejectAll(): void {
    const r = this.activeReport;
    if (!r) return;
    r.extractedViews.forEach((v) => {
      v.accepted = false;
      v.rejected = true;
    });
    this.cdr.markForCheck();
  }

  acceptHighConf(): void {
    const r = this.activeReport;
    if (!r) return;
    r.extractedViews.forEach((v) => {
      if (v.confidence >= 70) {
        v.accepted = true;
        v.rejected = false;
      }
    });
    this.cdr.markForCheck();
  }

  acceptMatched(): void {
    const r = this.activeReport;
    if (!r) return;
    r.extractedViews.forEach((v) => {
      if (v.matched === 'matched') {
        v.accepted = true;
        v.rejected = false;
      }
    });
    this.cdr.markForCheck();
  }

  applySelected(): void {
    const r = this.activeReport;
    if (!r) return;
    const count = r.extractedViews.filter((v) => v.accepted).length;
    if (!count) {
      this.toast('Accept at least one view to apply');
      return;
    }
    r.appliedCount = count;
    this.toast(`${count} view${count !== 1 ? 's' : ''} applied to My Views`);
    this.cdr.markForCheck();
  }

  openPdfPreview(v: ExtractedView): void {
    const r = this.activeReport;
    if (!r) return;
    const meta = this.sourceMeta(r);
    this.pdfPreviewContext = {
      source: r.source,
      sourceShort: meta.short,
      sourceLabel: r.sourceLabel,
      title: r.title,
      author: r.author,
      date: r.date,
      pageCount: r.pageCount,
      asset: v.asset,
      page: v.page,
      excerpt: v.excerpt,
    };
    this.pdfPreviewOpen = true;
    this.cdr.markForCheck();
  }

  closePdfPreview(): void {
    this.pdfPreviewOpen = false;
    this.cdr.markForCheck();
  }

  downloadFromPreview(): void {
    this.toast('PDF download started (demo)');
    this.closePdfPreview();
  }

  openUpload(): void {
    this.pdfDropOpen = true;
    this.cdr.markForCheck();
  }

  closeUpload(): void {
    this.pdfDropOpen = false;
    this.cdr.markForCheck();
  }

  onUploaded(filename: string): void {
    this.pdfDropOpen = false;
    this.toast(`"${filename}" imported — extracting views…`);
    setTimeout(() => this.restartWithFreshUpload(filename), 400);
  }

  private restartWithFreshUpload(filename: string): void {
    if (this.processingTimer) clearTimeout(this.processingTimer);
    this.reports = this.reports.filter((r) => r.status !== 'processing');
    const processing: ResearchReport = {
      id: 'fresh-' + Date.now(),
      title: filename,
      source: 'gavekal',
      sourceLabel: 'Processing…',
      author: 'Identifying source…',
      date: new Date().toISOString().slice(0, 10),
      pageCount: 0,
      status: 'processing',
      uploadedAgo: 'just now',
      summary:
        'Cimalti is extracting investment views, holdings, and performance data from your document.',
      viewsCount: 0,
      appliedCount: 0,
      extractedViews: [],
      fresh: true,
    };
    this.reports = [processing, ...this.reports];
    this.activeReportId = processing.id;
    this.cdr.markForCheck();

    this.processingTimer = setTimeout(() => {
      this.completeFreshUpload(processing.id, filename);
    }, 2200);
  }

  private completeFreshUpload(processingId: string, freshName: string | null): void {
    const demo = BASE_REPORTS[0];
    const idx = this.reports.findIndex((r) => r.id === processingId);
    if (idx < 0) return;
    const replacement: ResearchReport = {
      ...this.cloneReport(demo),
      id: processingId,
      title: freshName || demo.title,
      uploadedAgo: 'just now',
      appliedCount: 0,
      fresh: true,
    };
    replacement.extractedViews = replacement.extractedViews.map((v) => ({
      ...v,
      accepted: false,
      rejected: false,
    }));
    const next = [...this.reports];
    next[idx] = replacement;
    this.reports = next;
    this.activeReportId = replacement.id;
    this.toast('7 views extracted — ready for review');
    this.cdr.markForCheck();
  }

  private cloneReport(r: ResearchReport): ResearchReport {
    return { ...r, extractedViews: r.extractedViews.map((v) => ({ ...v })) };
  }

  private toast(text: string): void {
    const id = ++this.toastId;
    this.toasts = [...this.toasts, { id, text }];
    this.cdr.markForCheck();
    setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
      this.cdr.markForCheck();
    }, 2200);
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  goHome(): void {
    this.router.navigateByUrl('/app/home-experience');
  }
}
